import { useCallback, useEffect, useMemo, useState } from 'react';
import { orderBy } from 'lodash';
import { useSlate } from 'slate-react';
import { useThrottledCallback } from 'use-debounce';

import { getInstanceIdFromDocumentDomElement } from 'src/data/document';
import {
  DOCUMENT_LOCKED_SCORE_PANEL_HEIGHT,
  DOCUMENT_SCORE_PANEL_HEIGHT,
  DOCUMENT_SCORE_SIZE,
} from 'src/data/ui';
import { useResizeObserver } from 'src/lib/hooks';
import { useCustomerHasLiveScoring } from 'src/store';

const THROTTLE_INTERVAL = 100;
const MIN_SCORE_TOP = -24;
const SCORE_GAP = 3;

const sortBoundaries = (boundaries) => orderBy(boundaries, ['middle'], ['asc']);

const deduceScoreBlockHeight = (boundary, activeInstanceId) => {
  if (boundary.isScoreLocked) {
    return DOCUMENT_LOCKED_SCORE_PANEL_HEIGHT;
  }

  // Current score block height, is higher if it's currently active variation
  return boundary.instanceId === activeInstanceId
    ? DOCUMENT_SCORE_PANEL_HEIGHT
    : DOCUMENT_SCORE_SIZE;
};

const spreadBoundaries = (boundaries, activeInstanceId) => {
  let previousBottom = null;
  return boundaries.map((boundary) => {
    // Next empty vertical position, where we can position current score
    const freeTop = previousBottom ? previousBottom + SCORE_GAP : MIN_SCORE_TOP;

    const currentHeight = deduceScoreBlockHeight(boundary, activeInstanceId);

    // If current variation score is not overlapping with previous - don't do anything
    if (boundary.middle >= freeTop + currentHeight / 2) {
      previousBottom = boundary.middle + currentHeight / 2;
      return boundary;
    }

    previousBottom = freeTop + currentHeight;
    return { ...boundary, middle: freeTop + currentHeight / 2 };
  });
};

export const useDocumentEditorVariationsBoundaries = ({ parentElement, activeInstanceId }) => {
  const editor = useSlate();
  const hasLiveScoring = useCustomerHasLiveScoring();
  const [boundaries, setBoundaries] = useState([]);

  /**
   * Scanning operation runs in throttle mode, not quicker than each "THROTTLE_INTERVAL" ms
   */
  const updateDomBoundaries = useThrottledCallback(() => {
    if (!parentElement) {
      return;
    }

    // Get all existing "variation" DOM nodes
    const domVariations = parentElement.querySelectorAll('[data-variation]');
    const newBoundariesArray = [...domVariations].map((element) => ({
      instanceId: getInstanceIdFromDocumentDomElement(element),
      isScoreLocked: !hasLiveScoring && element.dataset.scoreLocked != null,
      middle: element.offsetTop + element.offsetHeight / 2,
    }));
    setBoundaries(newBoundariesArray);
  }, THROTTLE_INTERVAL);

  /**
   * Run scan each time something gets changed in editor's DOM
   */
  useEffect(() => {
    updateDomBoundaries();
  }, [editor.children, updateDomBoundaries]);

  /**
   * When parent size changes - recalculate boundaries as well
   */
  const handleResize = useCallback(() => {
    if (parentElement.offsetHeight > 0) {
      updateDomBoundaries();
    }
  }, [parentElement, updateDomBoundaries]);
  useResizeObserver(parentElement, handleResize);

  /**
   * Adjust boundaries, so scores won't overlap each other
   */

  return useMemo(() => {
    // Sort boundaries by 'top' field
    const sorted = sortBoundaries(boundaries);

    // Adjust vertical positions, so scores won't overlap
    const adjusted = spreadBoundaries(sorted, activeInstanceId);

    // Convert to object and store
    const mapped = adjusted.map(({ instanceId, ...restProps }) => [instanceId, restProps]);
    return Object.fromEntries(mapped);
  }, [boundaries, activeInstanceId]);
};
